// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@conklin/App/Components/Notify";
import xLoadingOverlay from "@conklin/App/Handlers/XNG/LoadingOverlay";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import { doScroll, xResetFormFieldsErros, xSiteMessage, xValidateOnSubmit } from "@conklin/XDL/Utilities";
import { maxLengthAttr } from "@conklin/XDL/Utilities/validation";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Change
const enum fields {
    customerTitle = "customerTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customerTitle]: string;
    };
}

const mapDispatchToProps = {
    addAccount,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
    // XNG-Changes
    loadCurrentCountries,
    xLoadingOverlay,
};

const mapStateToProps = (state: ApplicationState & CustomAppState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
    homePageUrl: getHomePageUrl(state),
    reCaptchaEnabledForCreateAccount: getSettingsCollection(state).websiteSettings.reCaptchaEnabledForCreateAccount,
    // XNG-Changes
    countries: getCurrentCountries(state),
});

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    activationInstructionGridItem?: GridItemProps;
    activationInstructionText?: TypographyPresentationProps;
    activationSuccessWrapper?: InjectableCss;
    activationSuccessTitleText?: TypographyPresentationProps;
    activationSuccessMessageText?: TypographyPresentationProps;
    activationSuccessHomeButton?: ButtonPresentationProps;
    // XNG-Changes
    cusomerText?: TypographyProps;
    formFieldGridItem?: GridItemProps;
    textGridItem?: GridItemProps;
    firstNameTextField?: TextFieldPresentationProps;
    lastNameTextField?: TextFieldPresentationProps;
    companyNameTextField?: TextFieldPresentationProps;
    customerTypeselect?: SelectPresentationProps;
    accountNumberTextField?: TextFieldPresentationProps;
    positionTextField?: TextFieldPresentationProps;
    addressTextField?: TextFieldPresentationProps;
    cityTextField?: TextFieldPresentationProps;
    stateSelect?: SelectPresentationProps;
    countrySelect?: SelectPresentationProps;
    zipCodeTextField?: TextFieldPresentationProps;
    phoneTextField?: TextFieldPresentationProps;
    faxTextField?: TextFieldPresentationProps;
    confirmEmailTextField?: TextFieldPresentationProps;
    errorMsg?: InjectableCss;
    customerRadioGridItem?: GridItemProps;
    customerRadioGroup?: RadioGroupPresentationProps;
}

export const createAccountStyles: CreateAccountStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    userGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 100px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    userGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 300px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    emailGridItem: {
        width: 12,
    },
    userNameGridItem: {
        width: 12,
    },
    passwordGridItem: {
        width: 12,
    },
    confirmPasswordGridItem: {
        width: 12,
    },
    subscriptionGridItem: {
        width: 12,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    requirementsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 50px;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    passwordRequirementsGridContainer: {
        gap: 5,
    },
    passwordRequirementsGridItem: {
        width: 12,
    },
    requirementsTitle: {
        variant: "h4",
        as: "h2",
    },
    activationInstructionGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 50px;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    activationSuccessWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            width: 50%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    activationSuccessTitleText: {
        variant: "h3",
    },
    activationSuccessHomeButton: {
        css: css`
            align-self: flex-end;
        `,
    },
};

const styles = createAccountStyles;
const StyledForm = getStyledWrapper("form");

const CreateAccount = ({
    addAccount,
    validateReCaptcha,
    // XNG-Changes
    countries,
    loadCurrentCountries,
    xLoadingOverlay,
    homePageUrl,
    reCaptchaEnabledForCreateAccount,
    history,
    fields,
}: Props) => {
    const initialFormData = {
        firstName: "",
        lastName: "",
        companyName: "",
        customerNumber: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
        phone: "",
        email: "",
        confirmEmail: "",
        country: countries?.[0]?.abbreviation,
    };

    const [errorMessage, setErrorMessage] = useState("");
    const [isActivationEmailSent, setIsActivationEmailSent] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();
    // XNG-Changes
    const [formValues, setFormValues] = useState({ ...initialFormData });
    const [requestOpenCreditCard, setRequestOpenCreditCard] = useState(false);
    const [confirmEmailErrorMessage, setConfirmEmailErrorMessage] = useState<ReactNode>("");
    const [isDefaultCustomer, setIsDefaultCustomer] = useState("false");
    const [country, setCountry] = useState<CountryModel>();
    const [stateErrorMessage, setStateErrorMessage] = useState("");
    const [customerNumberErrorMessage, setCustomerNumberErrorMessage] = useState("");
    const formId = "createAccountForm";

    // XNG-Changes
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = event.target as HTMLInputElement | HTMLSelectElement;
        setFormValues(prevValues => ({
            ...prevValues,
            [name]: value,
        }));

        if (name === "country") {
            const selectedCountry = countries?.find(c => c.abbreviation === value);
            setCountry(selectedCountry);
            setFormValues(prevValues => ({
                ...prevValues,
                state: "",
            }));
        }

        if (name === "state") {
            setStateErrorMessage("");
        }

        if (name === "customerNumber") {
            setCustomerNumberErrorMessage("");
        }

        if (name === "email" || name === "confirmEmail") {
            validateConfirmEmail(
                name === "email" ? value : formValues.email,
                name === "confirmEmail" ? value : formValues.confirmEmail,
            );
        }

        setIsSubmitted(false);
        setIsSubmitting(false);
    };

    // XNG-Changes: load countries
    useEffect(() => {
        loadCurrentCountries();
    }, []);

    useEffect(() => {
        if (countries?.length) {
            handleInputChange({
                target: {
                    value: countries[0]?.abbreviation,
                    name: "country",
                },
            } as any);
        }
    }, [countries]);

    // XNG-Changes
    const validateConfirmEmail = (newEmail: string, newConfirmEmail: string) => {
        if (!newEmail || !newConfirmEmail) {
            setConfirmEmailErrorMessage("");
            return true;
        }
        const errorMessage =
            newEmail !== newConfirmEmail
                ? siteMessage("Custom_CreateNewAccountInfo_EmailCombination_DoesNotMatch")
                : "";
        setConfirmEmailErrorMessage(errorMessage);

        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        const hasCustomerNumberError = isDefaultCustomer === "true" && formValues.customerNumber === "";
        return !(stateErrorMessage || confirmEmailErrorMessage || hasCustomerNumberError);
    };

    // XNG-Changes
    const confirmEmailChangeHandler = (newConfirmEmail: string) => {
        setFormValues(prevValues => ({
            ...prevValues,
            confirmEmail: newConfirmEmail,
        }));
        validateConfirmEmail(formValues.email, newConfirmEmail);
    };

    const onSuccessHandler = () => {
        setIsSubmitting(false);
        xLoadingOverlay({ key: "clear", id: "Create_Account" });
        setErrorMessage("");
        setFormValues({ ...initialFormData });
        // <XNG-Changes>
        const successMessage = xSiteMessage("Custom_Create_Account_Successfully") || translate("Account created successfully!");
        toasterContext.addToast({ body: successMessage, messageType: "success" });

        // </XNG-Changes>
        setTimeout(() => {
            homePageUrl && history.push(homePageUrl);
        }, 500);

        // if (account?.activationStatus === "Activated") {
        //     setTimeout(() => {
        //         signIn({
        //             userName: useEmailAsUserName ? email : userName,
        //             password,
        //             rememberMe: false,
        //             returnUrl,
        //         });
        //     }, 500);
        // } else {
        //     setIsActivationEmailSent(true);
        // }
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const customerNumberErrorMessage = !formValues.customerNumber ? "Customer Number is a required field" : "";
        const stateErrorMessage = !formValues.state ? "State is a required field" : "";

        setCustomerNumberErrorMessage(customerNumberErrorMessage);
        setStateErrorMessage(stateErrorMessage);

        if (!validateConfirmEmail(formValues.email, formValues.confirmEmail)) {
            doScroll();
            return;
        }

        if (xValidateOnSubmit(formId).length > 0) {
            return;
        }
        if (!validateSubmitEnabled()) {
            return;
        }

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }
        setIsSubmitting(true);
        const { confirmEmail, ...restFormData } = formValues;
        addAccount({
            ...restFormData,
            isSubscribed,
            requestOpenCreditCard,
            onSuccess: onSuccessHandler,
            onError: (error: string) => {
                setIsSubmitting(false);
                xLoadingOverlay({ key: "clear", id: "Create_Account" });
                setErrorMessage(error);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        } as any);
    };

    const handleCustomerRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedValue = e.currentTarget.value;
        setIsDefaultCustomer(selectedValue);
        setFormValues({
            ...initialFormData,
            ...(selectedValue === "false" ? { customerNumber: "" } : {}),
        });
        if (selectedValue === "true") {
            setRequestOpenCreditCard(false);
        }
        setIsSubmitted(false);
        setIsSubmitting(false);
        setCustomerNumberErrorMessage("");
        setStateErrorMessage("");
        setConfirmEmailErrorMessage("");
        setErrorMessage("");
        xResetFormFieldsErros(formId);
    };

    // const homeClickHandler = () => {
    //     history.push(homePageUrl);
    // };

    // if (isActivationEmailSent) {
    //     return (
    //         <StyledWrapper {...styles.activationSuccessWrapper}>
    //             <Typography as="h2" {...styles.activationSuccessTitleText}>
    //                 {translate("Check your email")}
    //             </Typography>
    //             <Typography {...styles.activationSuccessMessageText}>
    //                 {siteMessage("CreateNewAccountInfo_AccountActivation_SuccessMessage")}
    //             </Typography>
    //             <Button
    //                 {...styles.activationSuccessHomeButton}
    //                 onClick={homeClickHandler}
    //                 data-test-selector="createAccount_BackToHomePage"
    //             >
    //                 {translate("Home")}
    //             </Button>
    //         </StyledWrapper>
    //     );
    // }

    const isSubmitEnabled = validateSubmitEnabled();

    // XNG-Changes: custom fields were added
    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <GridContainer {...styles.mainGridContainer}>
                <GridItem {...styles.userGridItem}>
                    <GridContainer {...styles.userGridContainer}>
                        <GridItem {...styles.textGridItem}>
                            <Typography {...styles.cusomerText}>{fields.customerTitle}</Typography>
                        </GridItem>
                        <GridItem {...styles.customerRadioGridItem}>
                            <RadioGroup
                                as="legend"
                                value={isDefaultCustomer}
                                onChangeHandler={handleCustomerRadioChange}
                                {...styles.customerRadioGroup}
                            >
                                <Radio value="true" data-test-selector="createAccount_currentCMICustomer">
                                    {translate("Yes")}
                                </Radio>
                                <Radio value="false" data-test-selector="createAccount_notCurrentCMICustomer">
                                    {translate("No")}
                                </Radio>
                            </RadioGroup>
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                label={translate("Email Address")}
                                data-test-selector="createAccount_email"
                                placeholder={translate("eg. example@gmail.com")}
                                name="email"
                                value={formValues.email}
                                onChange={handleInputChange}
                                required
                                type="email"
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.confirmEmailTextField}
                                label={translate("Confirm Email Address")}
                                onChange={e => confirmEmailChangeHandler(e.currentTarget.value)}
                                value={formValues.confirmEmail}
                                error={confirmEmailErrorMessage}
                                placeholder={translate("eg. example@gmail.com")}
                                maxLength={256}
                                required
                                type="email"
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.firstNameTextField}
                                label={translate("First Name")}
                                placeholder={translate("First Name")}
                                name="firstName"
                                value={formValues.firstName}
                                onChange={handleInputChange}
                                required
                                type="firstName"
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.lastNameTextField}
                                label={translate("Last Name")}
                                placeholder={translate("Last Name")}
                                name="lastName"
                                value={formValues.lastName}
                                onChange={handleInputChange}
                                required
                                type="lastName"
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.companyNameTextField}
                                label={translate("Company Name")}
                                placeholder={translate("Company Name")}
                                name="companyName"
                                value={formValues.companyName}
                                onChange={handleInputChange}
                                required
                                type="text"
                                {...{ formId }}
                            />
                        </GridItem>
                        {isDefaultCustomer === "true" && (
                            <GridItem {...styles.formFieldGridItem}>
                                <TextField
                                    {...styles.accountNumberTextField}
                                    label={translate("Customer Number")}
                                    placeholder={translate("Customer Number")}
                                    name="customerNumber"
                                    value={formValues.customerNumber}
                                    onChange={handleInputChange}
                                    required
                                    maxLength={maxLengthAttr.customerNumber}
                                    type="text"
                                    error={isDefaultCustomer === "true" && customerNumberErrorMessage}
                                />
                            </GridItem>
                        )}
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.addressTextField}
                                label={translate("Address Line 1")}
                                placeholder={translate("Address Line 1")}
                                name="addressLine1"
                                value={formValues.addressLine1}
                                onChange={handleInputChange}
                                required
                                type="address"
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.addressTextField}
                                label={translate("Address Line 2")}
                                placeholder={translate("Address Line 2")}
                                name="addressLine2"
                                value={formValues.addressLine2}
                                onChange={handleInputChange}
                                type="address"
                                required={false}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.cityTextField}
                                label={translate("city")}
                                placeholder={translate("City")}
                                name="city"
                                value={formValues.city}
                                onChange={handleInputChange}
                                required
                                type="city"
                                {...{ formId }}
                            />
                        </GridItem>
                        {countries && (
                            <GridItem {...styles.formFieldGridItem}>
                                <Select
                                    label={translate("State")}
                                    {...styles.stateSelect}
                                    name="state"
                                    value={formValues.state}
                                    onChange={handleInputChange}
                                    error={stateErrorMessage}
                                    required
                                    {...{ formId }}
                                >
                                    <option value="Select State">{translate("Select State")}</option>
                                    {country?.states?.map(o => (
                                        <option key={o.abbreviation} value={o.abbreviation}>
                                            {o.name}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.zipCodeTextField}
                                label={translate("Zip Code")}
                                placeholder={translate("Zip Code")}
                                name="zipCode"
                                value={formValues.zipCode}
                                onChange={handleInputChange}
                                required
                                type={country?.name === "Canada" ? "postalCodeCA" : "postalCodeUS"}
                                maxLength={15}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.phoneTextField}
                                label={translate("Phone Number")}
                                placeholder="( ___ ) ___ - ____"
                                name="phone"
                                value={formValues.phone}
                                onChange={handleInputChange}
                                required
                                type="phone"
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.subscriptionGridItem}>
                            <CheckboxGroup>
                                <Checkbox
                                    onChange={(e, value) => setIsSubscribed(value)}
                                    checked={isSubscribed}
                                    data-test-selector="createAccount_Subscription"
                                >
                                    {siteMessage("SignIn_Sign_Up_Newsletter")}
                                </Checkbox>
                                {isDefaultCustomer === "false" && (
                                    <Checkbox
                                        onChange={(e, value) => setRequestOpenCreditCard(value)}
                                        checked={requestOpenCreditCard}
                                        data-test-selector="createAccount_createCreditAccount"
                                    >
                                        {siteMessage("Custom_CreateAccount_RequesOpenCreditCardAccount")}
                                    </Checkbox>
                                )}
                            </CheckboxGroup>
                        </GridItem>
                        {errorMessage && (
                            <GridItem data-test-selector="createAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                                <Notify {...styles.errorMsg} type="danger">
                                    {errorMessage}
                                </Notify>
                            </GridItem>
                        )}
                        {reCaptchaEnabledForCreateAccount && (
                            <GridItem {...styles.reCaptchaGridItem}>
                                <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                            </GridItem>
                        )}
                        <GridItem {...styles.submitGridItem}>
                            <Button
                                {...styles.submitButton}
                                type="submit"
                                disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                                data-test-selector="createAccount_createButton"
                                aria-label="Submit"
                            >
                                {translate("Submit")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CreateAccount)),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.customerTitle,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "Are you a current Conklin Metal Industries customer?",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
